import classnames from 'classnames2';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { space, SpaceProps } from 'styled-system';

import { ThemedProvider } from '../ThemedProvider';
import { IBaseProps } from '../utils';

import buttonStyles from './Button.module.scss';

type MarginSpaceProps = Omit<
  SpaceProps,
  | 'p'
  | 'padding'
  | 'pt'
  | 'paddingTop'
  | 'pr'
  | 'paddingRight'
  | 'pb'
  | 'paddingBottom'
  | 'pl'
  | 'paddingLeft'
  | 'px'
  | 'paddingX'
  | 'py'
  | 'paddingY'
>;

export interface IButtonProps extends MarginSpaceProps {
  buttonType?: 'button' | 'submit';
  title?: string;
  dataTest?: string;
  disabled?: boolean;
  selected?: boolean;
  loading?: boolean;
  tabIndex?: number;
  onClick?(event?);
}

export interface IButtonPropsNew extends MarginSpaceProps, IBaseProps, React.HTMLAttributes<HTMLElement> {
  /** Changes the screen reader label for the button. Defaults to "Loading" when loading state is true. */
  ariaLabel?: string;
  /** Changes the HTML element used to display the component.
   * Toggles support of onClick or href depending on which element is used.
   */
  as?: 'a' | 'button';
  buttonType?: 'button' | 'submit';
  /** Determines if button is disabled. */
  disabled?: boolean;
  href?: string;
  /** Whether the button is in a loading state or not.
   * When in a loading state, the button cannot be pressed and its text is replaced by a loading animation.
   */
  loading?: boolean;
  /** Determines if button is selected. */
  selected?: boolean;
  tabIndex?: number;
  /** Defines the size of the button */
  size?: 'default' | 'small' | 'large';
  /** Adds a `title` attribute to <button>, which can be used to provide screenreader users additional context */
  title?: string;
  /** Defines the style variant of the button */
  variant?: 'neutral' | 'primary' | 'destructive' | 'naked';
  inputMode?: 'none' | 'search' | 'numeric' | 'text' | 'email' | 'tel' | 'url' | 'decimal';
  /** Callback for when the button is clicked by the user.
   * If the button is disabled, the callback will not called.
   */
  onClick?(event?);
}
const StyledButton = styled.button`
  ${space}
`;

const ButtonComp: React.FC<IButtonPropsNew> = (
  {
    as = 'button',
    ariaLabel,
    buttonType = 'button',
    className = '',
    children,
    dataTest,
    disabled,
    href,
    loading,
    onClick,
    selected,
    size = 'default',
    title,
    variant = 'neutral',
    ...rest
  },
  ref?: any
) => {
  const isDisabled = disabled || loading;
  const stylesMap = classnames({
    [className]: className,
    [buttonStyles.stateLoading]: loading,
    [buttonStyles.stateDisabled]: disabled,
    [buttonStyles.stateSelected]: selected,
    [buttonStyles.variantNeutral]: variant === 'neutral',
    [buttonStyles.variantPrimary]: variant === 'primary',
    [buttonStyles.variantDestructive]: variant === 'destructive',
    [buttonStyles.variantNaked]: variant === 'naked',
    [buttonStyles.sizeDefault]: size === 'default',
    [buttonStyles.sizeSmall]: size === 'small',
    [buttonStyles.sizeLarge]: size === 'large'
  });

  const childrenContainerStyleMap = classnames({
    [buttonStyles.buttonChildrenContainerVisible]: !loading,
    [buttonStyles.buttonChildrenContainerNotVisible]: loading
  });

  return (
    <ThemedProvider>
      <StyledButton
        ref={ref}
        title={title}
        type={as === 'button' ? buttonType : null}
        data-test={dataTest}
        className={stylesMap}
        onClick={as === 'button' && !isDisabled ? onClick : null}
        href={as === 'a' ? href : null}
        disabled={isDisabled}
        aria-busy={loading}
        aria-label={loading ? 'Loading' : ariaLabel}
        aria-pressed={selected}
        {...rest}
      >
        {loading && <div data-test="loading-animation" aria-hidden={true} className={buttonStyles.loadingAnimation} />}
        <div className={childrenContainerStyleMap}>{children}</div>
      </StyledButton>
    </ThemedProvider>
  );
};

// Please delete once Voltron and Zeus are on the same version of typescript and use
// export const Button = forwardRef((props, ref) => ...
export const ButtonRefWrap = () => forwardRef(ButtonComp);
export const Button = ButtonRefWrap();

export const ButtonDefaultProps: Partial<IButtonProps> = {
  buttonType: 'button'
};

export type IButtonComponent = React.FC<IButtonProps>;

export type IButtonComponentNamespace = IButtonComponent & {
  Default: IButtonComponent;
  Large: IButtonComponent;
  Small: IButtonComponent;
};

export interface IButtonTypes {
  Destructive: IButtonComponentNamespace;
  Naked: IButtonComponentNamespace;
  Neutral: IButtonComponentNamespace;
  Primary: IButtonComponentNamespace;
}

// DO NOT export this in the public API, it is being exported for INTERNAL reuse ONLY - CL
export function createButton(...styles) {
  const ButtonBaseWithRef: IButtonComponent = (props, ref?: React.Ref<HTMLButtonElement>) => {
    const disabled = props.disabled || props.loading;
    const stylesMap = classnames(
      {
        [buttonStyles.stateLoading]: props.loading,
        [buttonStyles.stateDisabled]: props.disabled,
        [buttonStyles.stateSelected]: props.selected
      },
      ...styles
    );

    const childrenContainerStyleMap = classnames({
      [buttonStyles.buttonChildrenContainerVisible]: !props.loading,
      [buttonStyles.buttonChildrenContainerNotVisible]: props.loading
    });
    return (
      <ThemedProvider>
        <StyledButton
          tabIndex={props.tabIndex}
          title={props.title}
          type={props.buttonType || 'button'}
          data-test={props.dataTest}
          className={stylesMap}
          onClick={disabled ? null : props.onClick || null}
          disabled={disabled}
          ref={ref}
          aria-busy={props.loading}
          aria-label={props.loading ? 'Loading' : undefined}
          aria-pressed={props.selected}
          {...props}
        >
          {props.loading && (
            <div data-test="loading-animation" aria-hidden={true} className={buttonStyles.loadingAnimation} />
          )}
          <div className={childrenContainerStyleMap}>{props.children}</div>
        </StyledButton>
      </ThemedProvider>
    );
  };

  return React.forwardRef(ButtonBaseWithRef);
}

function createButtonNamespace(buttonStyle): IButtonComponentNamespace {
  const DefaultButton = createButton(buttonStyle, buttonStyles.sizeDefault);
  return Object.assign(DefaultButton, {
    Default: DefaultButton,
    Large: createButton(buttonStyle, buttonStyles.sizeLarge),
    Small: createButton(buttonStyle, buttonStyles.sizeSmall)
  });
}

export const ButtonDestructive = createButtonNamespace(buttonStyles.variantDestructive);
export const ButtonNaked = createButtonNamespace(buttonStyles.variantNaked);
export const ButtonNeutral = createButtonNamespace(buttonStyles.variantNeutral);
export const ButtonPrimary = createButtonNamespace(buttonStyles.variantPrimary);
